import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { studyNotesService } from '../../services/studyNotesService';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { QRCodeSVG } from 'qrcode.react';
import { StudyNotesSkeleton, FadeSlideIn } from '../../components/ui/SkeletonLoader';

// Card background styles: 0 = default, 1-3 = fancy
const CARD_BACKGROUNDS = [
  { label: 'Default', className: 'bg-card border border-border', style: {} },
  {
    label: 'Royal Gradient',
    className: 'border border-indigo-300/40',
    style: { background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 40%, #4338ca 70%, #6d28d9 100%)' },
    textClass: 'text-white',
    mutedClass: 'text-indigo-200',
    badgeClass: 'bg-white/10 text-indigo-100',
    iconClass: 'text-indigo-200',
  },
  {
    label: 'Dawn Glow',
    className: 'border border-orange-200/50',
    style: { background: 'linear-gradient(135deg, #fff7ed 0%, #fed7aa 35%, #fda4af 70%, #f9a8d4 100%)' },
    textClass: 'text-rose-900',
    mutedClass: 'text-rose-700',
    badgeClass: 'bg-white/40 text-rose-800',
    iconClass: 'text-rose-600',
  },
  {
    label: 'Forest Linen',
    className: 'border border-emerald-300/40',
    style: {
      background: 'linear-gradient(135deg, #064e3b 0%, #065f46 50%, #047857 100%)',
      backgroundImage: `linear-gradient(135deg, #064e3b 0%, #065f46 50%, #047857 100%), repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.03) 10px, rgba(255,255,255,0.03) 11px)`,
    },
    textClass: 'text-white',
    mutedClass: 'text-emerald-200',
    badgeClass: 'bg-white/10 text-emerald-100',
    iconClass: 'text-emerald-200',
  },
];

const HowToModal = ({ onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
    <div className="bg-card rounded-2xl p-6 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="BookOpen" size={18} color="var(--color-primary)" />
          </div>
          <h3 className="font-bold text-lg text-foreground">How to Use Study Notes</h3>
        </div>
        <button onClick={onClose} className="p-1.5 hover:bg-muted rounded-lg transition-colors">
          <Icon name="X" size={18} />
        </button>
      </div>

      <div className="space-y-5">
        {/* Step 1 */}
        <div className="flex gap-3">
          <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shrink-0 mt-0.5">1</div>
          <div>
            <p className="font-semibold text-foreground text-sm">Create a New Note</p>
            <p className="text-sm text-muted-foreground mt-0.5">Click <strong>"New Study Note"</strong> to open the editor. Give your note a title, add a speaker name, and choose your preferred Bible version.</p>
          </div>
        </div>

        {/* Step 2 */}
        <div className="flex gap-3">
          <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shrink-0 mt-0.5">2</div>
          <div>
            <p className="font-semibold text-foreground text-sm">Build Your Slides</p>
            <p className="text-sm text-muted-foreground mt-0.5">Add slides of different types — <strong>Title</strong>, <strong>Verse</strong>, or <strong>Text</strong>. For verse slides, type a reference like <em>John 3:16</em> and the text loads automatically. Notes auto-save as you work.</p>
          </div>
        </div>

        {/* Step 3 */}
        <div className="flex gap-3">
          <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shrink-0 mt-0.5">3</div>
          <div>
            <p className="font-semibold text-foreground text-sm">Present Your Notes</p>
            <p className="text-sm text-muted-foreground mt-0.5">Click <strong>"Present"</strong> on any note to open full-screen Presenter Mode — ideal for projectors or screens during a sermon or study session. Navigate slides with arrow keys or on-screen controls.</p>
          </div>
        </div>

        {/* Step 4 */}
        <div className="flex gap-3">
          <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shrink-0 mt-0.5">4</div>
          <div>
            <p className="font-semibold text-foreground text-sm">Share with Your Audience</p>
            <p className="text-sm text-muted-foreground mt-0.5">Use the <strong>QR Code</strong> button to generate a scannable code. Congregation members scan it to open the <strong>Audience View</strong> — a clean, read-only layout they can follow on their own devices in real time.</p>
          </div>
        </div>

        {/* Step 5 */}
        <div className="flex gap-3">
          <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shrink-0 mt-0.5">5</div>
          <div>
            <p className="font-semibold text-foreground text-sm">Store & Revisit</p>
            <p className="text-sm text-muted-foreground mt-0.5">All notes are saved to your account and accessible anytime from this page. Edit, re-present, or delete them whenever you need. Notes are private unless you share the audience link.</p>
          </div>
        </div>

        {/* Tip */}
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex gap-2">
          <Icon name="Lightbulb" size={16} color="var(--color-primary)" className="shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground"><strong className="text-foreground">Tip:</strong> Use <strong>Live Sync</strong> mode so audience slides advance automatically when you move to the next slide during your presentation.</p>
        </div>
      </div>

      <button
        onClick={onClose}
        className="mt-6 w-full px-4 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
      >
        Got it!
      </button>
    </div>
  </div>
);

const StudyNotesList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qrNote, setQrNote] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showHowTo, setShowHowTo] = useState(false);
  // Track background style index per note card (keyed by note id)
  const [cardBgMap, setCardBgMap] = useState({});

  useEffect(() => {
    if (user?.id) loadNotes();
  }, [user?.id]);

  const loadNotes = async () => {
    try {
      setLoading(true);
      const data = await studyNotesService?.getUserNotes(user?.id);
      setNotes(data);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (noteId) => {
    try {
      await studyNotesService?.deleteNote(noteId);
      setNotes(prev => prev?.filter(n => n?.id !== noteId));
      setDeleteConfirm(null);
    } catch (err) {
      setError(err?.message);
    }
  };

  const getLiveUrl = (slug) => `${window.location?.origin}/live/${slug}`;

  const cycleBg = (noteId) => {
    setCardBgMap(prev => {
      const next = ((prev?.[noteId] || 0) + 1) % CARD_BACKGROUNDS?.length;
      const updated = { ...prev, [noteId]: next };
      // Persist to localStorage so presenter can read the card theme
      try { localStorage.setItem('_fm_card_bg_map', JSON.stringify(updated)); } catch (_) {}
      return updated;
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-5xl mx-auto px-4 py-8 pb-24 md:pb-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground">Study Notes</h1>
            <p className="text-sm text-muted-foreground mt-1">Create and manage your sermon & study presentations</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowHowTo(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-muted hover:bg-muted/80 rounded-lg text-sm font-medium text-foreground transition-colors"
              title="How to use Study Notes"
            >
              <Icon name="HelpCircle" size={16} />
              <span className="hidden sm:inline">How To</span>
            </button>
            <Link
              to="/tools/notes/new"
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
            >
              <Icon name="Plus" size={16} />
              New Study Note
            </Link>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
            {error}
          </div>
        )}

        {loading ? (
          <StudyNotesSkeleton />
        ) : notes?.length === 0 ? (
          <div className="text-center py-16 bg-card rounded-xl border border-border">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="FileText" size={28} color="var(--color-primary)" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">No study notes yet</h3>
            <p className="text-muted-foreground text-sm mb-6">Create your first sermon or study presentation</p>
            <Link
              to="/tools/notes/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
            >
              <Icon name="Plus" size={16} />
              Create Study Note
            </Link>
          </div>
        ) : (
          <FadeSlideIn className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {notes?.map(note => {
              const bgIdx = cardBgMap?.[note?.id] || 0;
              const bg = CARD_BACKGROUNDS?.[bgIdx];
              const isDefault = bgIdx === 0;
              return (
                <div
                  key={note?.id}
                  className={`rounded-xl p-5 hover:shadow-md transition-shadow ${bg?.className}`}
                  style={bg?.style}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-semibold truncate ${isDefault ? 'text-foreground' : bg?.textClass}`}>{note?.title}</h3>
                      {note?.speaker && <p className={`text-xs mt-0.5 ${isDefault ? 'text-muted-foreground' : bg?.mutedClass}`}>{note?.speaker}</p>}
                    </div>
                    <div className="flex items-center gap-1 ml-2 shrink-0">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        note?.sync_mode === 'live' ? (isDefault ?'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-white/20 text-white')
                          : (isDefault ? 'bg-muted text-muted-foreground' : bg?.badgeClass)
                      }`}>
                        {note?.sync_mode === 'live' ? '● Live Sync' : 'Self-Paced'}
                      </span>
                    </div>
                  </div>
                  <div className={`flex items-center gap-3 text-xs mb-4 ${isDefault ? 'text-muted-foreground' : bg?.mutedClass}`}>
                    <span className="flex items-center gap-1">
                      <Icon name="Layers" size={12} />
                      {note?.slideCount} slides
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="Link" size={12} />
                      /live/{note?.slug}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="BookOpen" size={12} />
                      {note?.default_version}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/tools/notes/${note?.id}/edit`}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        isDefault ? 'bg-muted hover:bg-muted/80 text-foreground' : 'bg-white/15 hover:bg-white/25 text-white'
                      }`}
                    >
                      <Icon name="Edit2" size={13} />
                      Edit
                    </Link>
                    <Link
                      to={`/tools/notes/present/${note?.slug}`}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        isDefault ? 'bg-primary/10 hover:bg-primary/20 text-primary' : 'bg-white/20 hover:bg-white/30 text-white'
                      }`}
                    >
                      <Icon name="Play" size={13} />
                      Present
                    </Link>
                    <button
                      onClick={() => setQrNote(note)}
                      className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${
                        isDefault ? 'bg-muted hover:bg-muted/80' : 'bg-white/15 hover:bg-white/25'
                      }`}
                      title="Show QR Code"
                    >
                      <Icon name="QrCode" size={14} color={isDefault ? undefined : 'white'} />
                    </button>
                    <button
                      onClick={() => cycleBg(note?.id)}
                      className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors`}
                      style={isDefault ? {} : { background: 'rgba(255,255,255,0.15)' }}
                      title={`Card style: ${bg?.label} (click to change)`}
                    >
                      <Icon name="Palette" size={14} color={isDefault ? 'var(--color-primary)' : 'white'} />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(note?.id)}
                      className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${
                        isDefault ? 'bg-destructive/10 hover:bg-destructive/20' : 'bg-white/10 hover:bg-white/20'
                      }`}
                      title="Delete"
                    >
                      <Icon name="Trash2" size={14} color={isDefault ? 'var(--color-destructive)' : 'rgba(255,200,200,0.9)'} />
                    </button>
                  </div>
                </div>
              );
            })}
          </FadeSlideIn>
        )}
      </div>
      {/* How To Modal */}
      {showHowTo && <HowToModal onClose={() => setShowHowTo(false)} />}
      {/* QR Code Modal */}
      {qrNote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-card rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-foreground">QR Code</h3>
              <button onClick={() => setQrNote(null)} className="p-1 hover:bg-muted rounded-lg">
                <Icon name="X" size={18} />
              </button>
            </div>
            <p className="text-sm text-muted-foreground mb-4 truncate">{qrNote?.title}</p>
            <div className="flex justify-center p-4 bg-white rounded-xl mb-4">
              <QRCodeSVG value={getLiveUrl(qrNote?.slug)} size={200} />
            </div>
            <p className="text-xs text-center text-muted-foreground break-all">{getLiveUrl(qrNote?.slug)}</p>
            <button
              onClick={() => navigator.clipboard?.writeText(getLiveUrl(qrNote?.slug))}
              className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 bg-muted hover:bg-muted/80 rounded-lg text-sm transition-colors"
            >
              <Icon name="Copy" size={14} />
              Copy Link
            </button>
          </div>
        </div>
      )}
      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-card rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="font-semibold text-foreground mb-2">Delete Study Note?</h3>
            <p className="text-sm text-muted-foreground mb-6">This will permanently delete the note and all its slides. This cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg text-sm transition-colors">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 px-4 py-2 bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-lg text-sm transition-colors">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudyNotesList;
